
import { supabase } from "@/integrations/supabase/client";

/**
 * Run diagnostics on the database to check for WordPress posts
 * @returns Diagnostic results with information about database state
 */
export const diagnoseDatabase = async (): Promise<{
  success: boolean;
  postsCount?: number; 
  hasWordPressPosts?: boolean;
  schema?: string[];
  error?: string;
  code?: string;
  samplePosts?: any[];
}> => {
  try {
    console.log("Running database diagnostics...");
    
    // Check if the blog_posts table exists and count posts
    // Use a different approach to count posts that doesn't use aggregate functions
    const { data: posts, error: postsError } = await supabase
      .from("blog_posts")
      .select("id");
    
    if (postsError) {
      console.error("Error counting posts:", postsError);
      return {
        success: false,
        error: `Error counting posts: ${postsError.message}`,
        code: postsError.code
      };
    }
    
    const postsCount = posts ? posts.length : 0;
    console.log(`Found ${postsCount} posts in database`);
    
    // Check if there are any WordPress posts
    const { data: wpPosts, error: wpPostsError } = await supabase
      .from("blog_posts")
      .select("id, title")
      .not("wordpress_id", "is", null)
      .limit(5);
    
    if (wpPostsError && wpPostsError.message !== "No rows found") {
      console.error("Error checking for WordPress posts:", wpPostsError);
      return {
        success: false,
        error: `Error checking for WordPress posts: ${wpPostsError.message}`,
        code: wpPostsError.code
      };
    }
    
    const hasWordPressPosts = wpPosts && wpPosts.length > 0;
    console.log(`Has WordPress posts: ${hasWordPressPosts}, found ${wpPosts?.length || 0}`);
    
    // Get sample posts to check structure
    const { data: samplePosts, error: sampleError } = await supabase
      .from("blog_posts")
      .select("id, title, wordpress_id, created_at, slug, featured_image")
      .order("created_at", { ascending: false })
      .limit(3);
    
    if (sampleError && sampleError.message !== "No rows found") {
      console.error("Error getting sample posts:", sampleError);
      // Continue despite this error
    }
    
    // Check schema (this is a simplified approach - in a real app we'd use introspection)
    let schema: string[] = [];
    try {
      // This is a workaround to get column names since we don't have direct introspection
      if (samplePosts && samplePosts.length > 0) {
        schema = Object.keys(samplePosts[0]);
      }
    } catch (error) {
      console.error("Error checking schema:", error);
    }
    
    console.log("Diagnostics complete:", { 
      postsCount, 
      hasWordPressPosts,
      schema,
      samplePosts: samplePosts || []
    });
    
    return {
      success: true,
      postsCount,
      hasWordPressPosts,
      schema,
      samplePosts: samplePosts || []
    };
  } catch (error) {
    console.error("Error in diagnoseDatabase:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error in diagnostics"
    };
  }
};
